<script setup lang="ts">

import AddressFormInput from "@/shared/components/address/AddressFormInput.vue";
import {ref} from "vue";
import {Address, AddressInput, Country, Validation} from "@/shared/graphql/types";
import SubmitButton from "@/shared/components/SubmitButton.vue";
import {Form, validationToErrors2} from "@/shared/components/ui/form2/element";
import ValidationAlert from "@/shared/components/ValidationAlert.vue";
import {toast} from "vue-sonner";
import {useI18n} from 'vue-i18n'
import {addressService} from "@/shared/services/address.service.ts";
import {addressToInput, defaultAddressValidationSchema} from "@/shared/components/address/index.ts";

const {t} = useI18n()

/**
 * This dialog must be independent of the tenantStore because it can also be used in the checkout
 */
interface Props {
  customerId: string
  countries?: Country[]
  isHouseNumberSeparate: boolean
}

const props = withDefaults(defineProps<Props>(), {})
const loading = ref<boolean>(false)
const validation = ref<Validation>()
const errors = ref()

const emit = defineEmits<{
  (e: 'process:finished', address: Address): void
  (e: 'address:updated', address: Address): void
}>()

const validationSchema = defaultAddressValidationSchema(props.isHouseNumberSeparate)
const addressInput = ref<AddressInput>(addressToInput());

const onSubmit = async () => {
  loading.value = true
  const result = await addressService.createAddress(props.customerId, addressInput.value);

  loading.value = false
  if (result.validation.success) {
    toast.success(t('shared.address.success'))
    emit("process:finished", result.address)
    emit("address:updated", result.address)
  } else {
    validation.value = result.validation
    errors.value = validationToErrors2(result.validation)
  }
}
</script>

<template>
  <ValidationAlert v-if="validation" :validation="validation" class="mb-default"/>
  <Form @submit="onSubmit"
        v-model="addressInput"
        :errors="errors"
        :validation="validationSchema"
        id="address-update-form"
        :columns="1"
  >
    <AddressFormInput
        v-model="addressInput"
        :countries="countries"
        :is-house-number-separate="isHouseNumberSeparate"
    />
    <div class="col-span-full flex justify-end mt-default gap-default">
      <SubmitButton :loading="loading" data-tests="save"/>
    </div>
  </Form>
</template>